import "twin.macro";

import { Field } from "@headlessui/react";
import { Trash } from "lucide-react";
import { Fragment } from "react";
import {
  Control,
  FieldArrayPath,
  useFieldArray,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CreateAreaValues, UpdateAreaValues } from "@/api/areas/mutations";
import { Button } from "@/components/buttons";
import Input from "@/components/inputs";
import { StyledLabel } from "@/components/label";

type AreaValues = CreateAreaValues | UpdateAreaValues;

export const GatewaysFieldArray = <T extends AreaValues>({
  control,
  register,
}: {
  control: Control<T>;
  register: UseFormRegister<T>;
}) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray<T, FieldArrayPath<T>>({
    control,
    name: "gateways" as FieldArrayPath<T>,
  });

  return (
    <Fragment>
      <div tw="flex flex-col gap-1">
        <div tw="grid grid-cols-7 items-center gap-1">
          <StyledLabel tw="col-span-2">{t("form.addArea.address")}</StyledLabel>
          <StyledLabel tw="col-span-2">
            {t("form.addArea.latitude")}
          </StyledLabel>
          <StyledLabel tw="col-span-2">
            {t("form.addArea.longitude")}
          </StyledLabel>
        </div>

        {fields.map((field, index) => (
          <div key={field.id} tw="grid grid-cols-7 items-center gap-1">
            <Field tw="col-span-2">
              <Input
                tw="max-w-full"
                {...register(
                  // @ts-expect-error : gateways is in  AreaValues type but isn't recognized
                  `gateways.${index}.name` as const,
                  {
                    required: true,
                  }
                )}
              />
            </Field>
            <Field tw="col-span-2">
              <Input
                tw="max-w-full"
                {...register(
                  // @ts-expect-error : gateways is in  AreaValues type but isn't recognized
                  `gateways.${index}.latitude` as const
                )}
              />
            </Field>
            <Field tw="col-span-2">
              <Input
                tw="max-w-full"
                {...register(
                  // @ts-expect-error : gateways is in  AreaValues type but isn't recognized
                  `gateways.${index}.longitude` as const
                )}
              />
            </Field>

            <Button
              className="group"
              type="button"
              disabled={fields.length <= 1}
              tw="text-gray-500 hocus:text-gray-800"
              onClick={() => remove(index)}
            >
              <span tw="transition m-auto group-hocus:(scale-125 rotate-6)">
                <Trash tw="h-5 w-5" />
                <span tw="sr-only">Delete</span>
              </span>
            </Button>
          </div>
        ))}
      </div>

      <div tw="flex w-full justify-end">
        <Button
          green
          type="button"
          tw="w-fit"
          onClick={() =>
            append({
              address: 0,
              latitude: "",
              longitude: "",
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any)
          }
        >
          Ajouter une gateway +
        </Button>
      </div>
    </Fragment>
  );
};

export const PostsFieldArray = ({
  control,
  register,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<UpdateAreaValues, any>;
  register: UseFormRegister<UpdateAreaValues>;
}) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray<UpdateAreaValues, "posts">({
    control,
    name: "posts",
  });

  return (
    <Fragment>
      <div tw="flex flex-col gap-1">
        <div tw="grid grid-cols-9 items-center gap-1">
          <StyledLabel tw="col-span-2">{t("form.addPost.name")}</StyledLabel>
          <StyledLabel tw="col-span-2">{t("form.addPost.address")}</StyledLabel>
          <StyledLabel tw="col-span-2">
            {t("form.addArea.latitude")}
          </StyledLabel>
          <StyledLabel tw="col-span-2">
            {t("form.addArea.longitude")}
          </StyledLabel>
        </div>

        {fields.map((field, index) => (
          <div key={field.id} tw="grid grid-cols-9 items-center gap-1">
            <Field tw="col-span-2">
              <Input
                tw="max-w-full"
                {...register(`posts.${index}.name`, {
                  required: true,
                })}
              />
            </Field>
            <Field tw="col-span-2">
              <Input
                tw="max-w-full"
                {...register(`posts.${index}.address`, {
                  required: true,
                })}
              />
            </Field>
            <Field tw="col-span-2">
              <Input tw="max-w-full" {...register(`posts.${index}.latitude`)} />
            </Field>
            <Field tw="col-span-2">
              <Input
                tw="max-w-full"
                {...register(`posts.${index}.longitude`)}
              />
            </Field>

            <Button
              className="group"
              type="button"
              disabled={fields.length <= 1}
              tw="text-gray-500 hocus:text-gray-800"
              onClick={() => remove(index)}
            >
              <span tw="transition m-auto group-hocus:(scale-125 rotate-6)">
                <Trash tw="h-5 w-5" />
                <span tw="sr-only">Delete</span>
              </span>
            </Button>
          </div>
        ))}
      </div>

      <div tw="flex w-full justify-end">
        <Button
          green
          type="button"
          tw="w-fit"
          onClick={() =>
            append({
              name: "",
              address: 0,
              latitude: "",
              longitude: "",
              status: "closed",
            })
          }
        >
          Ajouter un poteau +
        </Button>
      </div>
    </Fragment>
  );
};
